import { Box, Grid, Typography, Card, CircularProgress, Chip } from '@mui/material'
import CustomButton from 'components/Button'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetProjectSettingsQuery } from 'store/slices/apis/projectSettingsSlice'
import UpdateProjectSettings from './UpdateProjectSettings'
import { formatKeysToLabel } from 'utils/reusable'
import moment from 'moment'
import { DEFAULT_DATE_FORMAT } from 'config'

const keysNotToShow = ["projectId"];
const ProjectSettings = () => {
    const { projectId } = useParams()
    const { data, isFetching, isError } = useGetProjectSettingsQuery({ projectId })
    const [openModal, setOpenModal] = useState(false);
    const settings = Object.entries(data?.data || {}).filter(([key]) => !keysNotToShow.includes(key));
    const configuredCount = settings.filter(([, value]) => value !== null && value !== "").length;
    const updatedDate = data?.data?.updatedDate;

    const formatValue = (key, value) => {
        if (typeof value === "boolean") {
            return <Chip label={value ? "Enabled" : "Disabled"} size="small" sx={{ height: 20, borderRadius: "999px", bgcolor: value ? "#E9FBF3" : "#F1F5F9", color: value ? "#16A36A" : "#64748B", fontSize: 10, fontWeight: 650 }} />;
        }
        if (value === null || value === "") return "Not configured";
        if (key === "updatedDate") return moment(value).format(DEFAULT_DATE_FORMAT);
        return String(value);
    };


    return (
        <>
            <Box sx={{ px: 2, py: 1.75, bgcolor: "#FFFFFF", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 650, fontSize: "20px", lineHeight: "27px", color: "#0F172A", fontFamily: "Outfit, sans-serif" }}>
                        Project Settings
                    </Typography>
                    <Typography sx={{ mt: 0.2, fontWeight: 400, fontSize: "12px", lineHeight: "17px", color: "#64748B", fontFamily: "Outfit, sans-serif" }}>
                        Configure project-level controls and operational defaults.
                    </Typography>
                </Box>
                <CustomButton label="Edit Settings" onClick={() => setOpenModal(true)} sx={{ minHeight: 36, px: 2, py: 0.8, fontSize: 13, fontWeight: 600, flexShrink: 0 }} />
            </Box>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, border: "1px solid #E2E8F0", borderRadius: "3px", bgcolor: "#FFFFFF" }}>
                        {[
                            { label: "Available settings", value: settings.length, color: "#2563EB" },
                            { label: "Configured", value: configuredCount, color: "#16A36A" },
                            { label: "Last updated", value: updatedDate ? moment(updatedDate).format(DEFAULT_DATE_FORMAT) : "Not available", color: "#E59A16" },
                        ].map((item, index) => (
                            <Box key={item.label} sx={{ px: 1.5, py: 1.25, borderRight: { sm: index < 2 ? "1px solid #E2E8F0" : 0 }, borderBottom: { xs: index < 2 ? "1px solid #E2E8F0" : 0, sm: 0 } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: item.color }} /><Typography sx={{ fontSize: 10, fontWeight: 650, textTransform: "uppercase", color: "#64748B" }}>{item.label}</Typography></Box>
                                <Typography sx={{ mt: 0.25, fontSize: 17, lineHeight: "23px", fontWeight: 650, color: "#0F172A" }}>{item.value}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {isFetching && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "150px"
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    <Card elevation={0} sx={{ width: "100%", mt: 2, borderRadius: "3px", border: "1px solid #E2E8F0" }}>
                        {!isFetching && isError && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "150px"
                                }}
                            >
                                <Typography color="error" fontWeight={500}>
                                    Failed to load project settings
                                </Typography>
                            </Box>
                        )}

                        {!isFetching && !isError && <>
                            <Box sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid #E2E8F0" }}>
                                <Typography sx={{ fontSize: 15, lineHeight: "21px", fontWeight: 650, color: "#0F172A" }}>Settings registry</Typography>
                                <Typography sx={{ mt: 0.2, fontSize: 11, lineHeight: "16px", color: "#64748B" }}>Current values applied to this project.</Typography>
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
                                {settings.map(([key, value], index) => (
                                    <Box key={key} sx={{ px: 1.5, py: 1.25, minHeight: 64, borderBottom: "1px solid #E2E8F0", borderRight: { md: index % 2 === 0 ? "1px solid #E2E8F0" : 0 } }}>
                                        <Typography sx={{ fontSize: 11, lineHeight: "16px", fontWeight: 600, color: "#64748B" }}>{formatKeysToLabel(key)}</Typography>
                                        <Box sx={{ mt: 0.5, fontSize: 13, lineHeight: "18px", fontWeight: 600, color: value === null || value === "" ? "#94A3B8" : "#0F172A" }}>{formatValue(key, value)}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </>}
                    </Card>

                </Grid>
            </Grid>
            <UpdateProjectSettings
                data={data}
                open={openModal}
                onClose={() => setOpenModal(false)}
            />
        </>
    )
}

export default ProjectSettings
